import React, { useState, useEffect } from 'react';
import { useProperty } from "../context/PropertyContext";
import DatePicker from "react-datepicker";
import { IP } from '../configuracion.js';
import "react-datepicker/dist/react-datepicker.css";
import '../styles/VentanaRevenue.css';

const VentanaRevenue = () => {  
  const [revenueData, setRevenueData] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [numberFormat, setNumberFormat] = useState('en-US');
  const [currency, setCurrency] = useState('USD');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { property } = useProperty();

  // Escuchar eventos de selección de mesa ocupada
  useEffect(() => {
    const handleTableSelected = () => {
      console.log("Actualizando datos por selección de mesa ocupada");
      setRefreshTrigger(prev => prev + 1); // Forzar actualización
    };

    window.addEventListener('tableSelected', handleTableSelected);
    return () => {
      window.removeEventListener('tableSelected', handleTableSelected);
    };
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        if (!property) {
          throw new Error("Propiedad no definida para cargar configuración.");
        }
        const response = await fetch(
          `${IP}/tavpos/backend/get_timezone.php?property=${encodeURIComponent(property)}`
        );
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error al cargar configuración (${response.status}): ${errorText}`);
        }
        const data = await response.json();
        if (!data.success || !data.data) {
          throw new Error(data.message || "No se pudo obtener la configuración.");
        }
        setNumberFormat(data.data.numberFormat || 'en-US');
        setCurrency(data.data.currency || 'USD');
      } catch (error) {
        console.error("Error al cargar configuración:", error);
        setError(error.message || "Error al cargar configuración. Usando en-US y USD como fallback.");
      }
    };

    const fetchData = async () => {
      if (!property) {
        console.warn("Propiedad no definida, usando URLs estáticas.");
        return;
      }
      try {
        setLoading(true);
        const response = await fetch(
          `${IP}/tavpos/backend/lectura_cheques.php?property=${encodeURIComponent(property)}`
        );
        if (!response.ok) throw new Error(`Error HTTP: ${response.status}`);
        const data = await response.json();

        if (data.success) {
          setRawData(data.data);

          const uniqueDates = [
            ...new Set(
              data.data
                .map((c) => c.fecha_control)
                .filter((dateStr) => dateStr && typeof dateStr === 'string' && !isNaN(new Date(dateStr).getTime()))
            ),
          ];

          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const sortedDates = uniqueDates.sort((a, b) => {
            const dateA = new Date(a);
            const dateB = new Date(b);
            return Math.abs(dateA - today) - Math.abs(dateB - today);
          });

          setAvailableDates(sortedDates);

          if (sortedDates.length > 0 && !startDate) {
            const closestDate = sortedDates[0];
            setStartDate(closestDate);
            setEndDate(closestDate);
          }

          const processed = processChequesData(data.data);
          setRevenueData(processed.revenueData);
          setTotals(processed.totals);
        } else {
          throw new Error(data.message || 'Error en los datos recibidos');
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig().then(fetchData);
  }, [property, refreshTrigger]); // Añadido refreshTrigger como dependencia

  // Optimización: Memoizar el procesamiento de datos
  const processChequesData = React.useCallback((cheques) => {
    const revenueMap = {};
    let allClientesSum = 0;

    const filteredCheques = startDate && endDate
      ? cheques.filter(cheque => {
          const chequeDate = new Date(cheque.fecha_control);
          const start = new Date(startDate);
          const end = new Date(endDate);
          return chequeDate >= start && chequeDate <= end;
        })
      : cheques;

    filteredCheques.forEach(cheque => {
      const revenueId = cheque.revenue_id;
      const revenueName = cheque.revenue;

      if (!revenueMap[revenueId]) {
        revenueMap[revenueId] = {
          revenueId,
          revenueName,
          totalSales: 0,
          chequeCount: 0,
          clientesSum: 0,
          totalTaxes: 0,
          totalDiscounts: 0,
        };
      }

      const revenue = revenueMap[revenueId];
      revenue.totalSales += parseFloat(cheque.total_cheque) || 0;
      revenue.chequeCount += 1;

      if (cheque.cliente !== null && cheque.cliente !== undefined) {
        const clienteValue = parseFloat(cheque.cliente) || 0;
        revenue.clientesSum += clienteValue;
        allClientesSum += clienteValue;
      }

      if (cheque.pedidos) {
        cheque.pedidos.forEach(pedido => {
          pedido.productos?.forEach(producto => {
            revenue.totalTaxes += parseFloat(producto.impuesto) || 0;
            revenue.totalDiscounts += parseFloat(producto.monto_descuento) || 0;

            producto.modificadores?.forEach(modificador => {
              revenue.totalTaxes += parseFloat(modificador.impuesto) || 0;
              revenue.totalDiscounts += parseFloat(modificador.monto_descuento) || 0;
            });
          });
        });
      }
    });

    const revenueArray = Object.values(revenueMap).map(revenue => {
      const netTotal = revenue.totalSales - revenue.totalDiscounts + revenue.totalTaxes;
      return {
        ...revenue,
        clientesInfo: revenue.clientesSum,
        averageCheque: revenue.chequeCount > 0 ? netTotal / revenue.chequeCount : 0
      };
    });

    const totals = {
      totalSales: revenueArray.reduce((sum, r) => sum + r.totalSales, 0),
      chequeCount: revenueArray.reduce((sum, r) => sum + r.chequeCount, 0),
      clientesSum: allClientesSum,
      totalTaxes: revenueArray.reduce((sum, r) => sum + r.totalTaxes, 0),
      totalDiscounts: revenueArray.reduce((sum, r) => sum + r.totalDiscounts, 0),
      get netTotal() {
        return this.totalSales - this.totalDiscounts + this.totalTaxes;
      },
      get averageCheque() {
        return this.chequeCount > 0 ? this.netTotal / this.chequeCount : 0;
      }
    };

    return { revenueData: revenueArray, totals };
  }, [startDate, endDate]);

  useEffect(() => {
    if (rawData.length > 0) {
      const processed = processChequesData(rawData);
      setRevenueData(processed.revenueData);
      setTotals(processed.totals);
    }
  }, [startDate, endDate, rawData, processChequesData]);

  const handleDateTabClick = (dateStr) => {
    setStartDate(dateStr);
    setEndDate(dateStr);
  };

  const parseDateString = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(numberFormat, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) return <div className="loading">Cargando datos...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="ventana-revenue">
      <div className="report-header">
        <h2>Reporte de Ventas por Centro de Consumo</h2>
      </div>

      <div className="date-container">
        <div className="date-inner-container">
          <div className="date-tabs-container">
            {availableDates.map((dateStr) => (
              <button
                key={dateStr}
                className={startDate === dateStr && endDate === dateStr ? "active" : ""}
                onClick={() => handleDateTabClick(dateStr)}
              >
                {dateStr}
              </button>
            ))}
          </div>

          <div className="date-picker-container">
            <DatePicker
              selected={startDate ? parseDateString(startDate) : null}
              onChange={(date) => setStartDate(date ? formatDate(date) : null)}
              selectsStart
              startDate={startDate ? parseDateString(startDate) : null}
              endDate={endDate ? parseDateString(endDate) : null}
              includeDates={availableDates.map(parseDateString)}
              dateFormat="yyyy-MM-dd"
              placeholderText="Fecha inicio"
              className="date-picker-input"
            />
            <DatePicker
              selected={endDate ? parseDateString(endDate) : null}
              onChange={(date) => setEndDate(date ? formatDate(date) : null)}
              selectsEnd
              startDate={startDate ? parseDateString(startDate) : null}
              endDate={endDate ? parseDateString(endDate) : null}
              minDate={startDate ? parseDateString(startDate) : null}
              includeDates={availableDates.map(parseDateString)}
              dateFormat="yyyy-MM-dd"
              placeholderText="Fecha fin"
              className="date-picker-input"
            />
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table>
          <thead>
            <tr>
              <th>Centro de Consumo</th>
              <th>Info. Clientes</th>
              <th>Ventas Totales</th>
              <th>Cheques</th>
              <th>Cheque Promedio</th>
              <th>Impuestos</th>
              <th>Descuentos</th>
              <th>Total Neto</th>
            </tr>
          </thead>
          <tbody>
            {revenueData.map(revenue => (
              <tr key={revenue.revenueId}>
                <td>{revenue.revenueName}</td>
                <td>{revenue.clientesInfo}</td>
                <td>{formatCurrency(revenue.totalSales)}</td>
                <td>{revenue.chequeCount}</td>
                <td>{formatCurrency(revenue.averageCheque)}</td>
                <td>{formatCurrency(revenue.totalTaxes)}</td>
                <td>{formatCurrency(revenue.totalDiscounts)}</td>
                <td>{formatCurrency(revenue.totalSales - revenue.totalDiscounts + revenue.totalTaxes)}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td>Total General</td>
              <td>{totals.clientesSum}</td>
              <td>{formatCurrency(totals.totalSales)}</td>
              <td>{totals.chequeCount}</td>
              <td>{formatCurrency(totals.averageCheque)}</td>
              <td>{formatCurrency(totals.totalTaxes)}</td>
              <td>{formatCurrency(totals.totalDiscounts)}</td>
              <td>{formatCurrency(totals.netTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Función auxiliar para formatear fechas
const formatDate = (date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export default VentanaRevenue;